
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import YouTube from 'react-youtube';
import PlayerModal from '../components/PlayerModal';
import { apiBaseUrl, isSyncBackendAvailable } from '../services/api';
import { getActivePlaylistSnapshot, resolveNextTrack } from '../utils/playlistUtils';
import { getAppTitle } from '../utils/version';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

function Player({
  updateUserField, 
  currentPlaylistItem, 
  currentTransitionVideo, 
  currentIntroVideo, 
  allowInteractivity = true, 
  showControlsPlayer, 
  setVideoId, 
  videoId,
  isIntegrated = false,
  userId: propsUserId,
  continuousPlay: propsContinuousPlay,
  crossfadeEnabled: propsCrossfadeEnabled,
  crossfadeDuration: propsCrossfadeDuration,
  onPlayNextSong
}) {

  const [openModal, setOpenModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.title = getAppTitle('Garaoke Player');
  }, []);
  const queryParams = new URLSearchParams(location.search);
  const getStoredHostUid = () => {
    try {
      const hostId = localStorage.getItem('garaoke_host_id');
      if (hostId) return hostId;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('firebase:authUser:')) {
          const fbData = JSON.parse(localStorage.getItem(key));
          if (fbData?.uid) return fbData.uid;
        }
      }
      const saved = localStorage.getItem('garaoke_user');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.uid) return parsed.uid;
      }
    } catch (e) {}
    return null;
  };

  const effectiveHostId = propsUserId 
    || queryParams.get('userId') 
    || queryParams.get('UID') 
    || queryParams.get('uid') 
    || getStoredHostUid();

  useEffect(() => {
    if (effectiveHostId) {
      try {
        localStorage.setItem('garaoke_host_id', effectiveHostId);
      } catch (e) {}
    }
  }, [effectiveHostId]);
  const [modalContentType, setModalContentType] = useState('');
  const [message, setMessage] = useState(null);
  const [intervalId, setIntervalId] = useState(null);
  const [queryQR, setQueryQR] = useState('');
  const [height, setHeight] = useState(window.innerHeight - 10);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef(null);
  const [currentIntroVideo2, setCurrentIntroVideo2] = useState(currentIntroVideo);
  const [currentTransitionVideo2, setCurrentTransitionVideo2] = useState(currentTransitionVideo);
  const [currentPlaylistItem2, setCurrentPlaylistItem2] = useState(currentPlaylistItem);

  const [continuousPlay, setContinuousPlay] = useState(() => {
    if (propsContinuousPlay !== undefined) return propsContinuousPlay;
    const q = queryParams.get('continuous');
    if (q !== null) return q === 'true';
    try { return localStorage.getItem('garaoke_continuous_play') === 'true'; } catch (e) { return false; }
  });

  const [crossfadeEnabled, setCrossfadeEnabled] = useState(() => {
    if (propsCrossfadeEnabled !== undefined) return propsCrossfadeEnabled;
    const q = queryParams.get('crossfade');
    if (q !== null) return q === 'true';
    try { return localStorage.getItem('garaoke_crossfade_enabled') === 'true'; } catch (e) { return false; }
  });

  const [crossfadeDuration, setCrossfadeDuration] = useState(() => {
    if (propsCrossfadeDuration !== undefined) return propsCrossfadeDuration;
    const q = queryParams.get('crossfadeDuration');
    if (q !== null) return Number(q);
    try {
      const v = Number(localStorage.getItem('garaoke_crossfade_duration'));
      return !isNaN(v) && v >= 2 && v <= 15 ? v : (Number(import.meta.env?.VITE_DEFAULT_CROSSFADE_SEC) || 5);
    } catch (e) { return 5; }
  });

  const nextSongRef = useRef(null);
  const isEndOfPlaylistRef = useRef(false);
  const crossfadingRef = useRef(false);
  const precuedVideoIdRef = useRef(null);
  const precuedStartSecRef = useRef(0);
  const precuedDeckRef = useRef(null);

  useEffect(() => {
    if (propsContinuousPlay !== undefined) setContinuousPlay(propsContinuousPlay);
  }, [propsContinuousPlay]);

  useEffect(() => {
    if (propsCrossfadeEnabled !== undefined) setCrossfadeEnabled(propsCrossfadeEnabled);
  }, [propsCrossfadeEnabled]);

  useEffect(() => {
    if (propsCrossfadeDuration !== undefined) setCrossfadeDuration(propsCrossfadeDuration);
  }, [propsCrossfadeDuration]);

  const defaultFallback = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
  const [initialStartTime] = useState(() => {
    if (currentPlaylistItem?.item?.startTime !== undefined && currentPlaylistItem?.item?.startTime !== null) {
      return Math.max(0, Number(currentPlaylistItem.item.startTime) || 0);
    }
    const q = queryParams.get('startTime') || queryParams.get('t');
    return q !== null && !isNaN(Number(q)) ? Math.max(0, Number(q)) : 0;
  });
  const [initialEndTime] = useState(() => {
    if (currentPlaylistItem?.item?.endTime !== undefined && currentPlaylistItem?.item?.endTime !== null) {
      return Math.max(0, Number(currentPlaylistItem.item.endTime) || 0);
    }
    const q = queryParams.get('endTime');
    return q !== null && !isNaN(Number(q)) ? Math.max(0, Number(q)) : 0;
  });
  const [initialVideoId] = useState(() => {
    const raw = videoId || queryParams.get('videoId') || currentTransitionVideo;
    return (raw && raw !== 'undefined') ? raw : defaultFallback;
  });
  const currentVideoIdRef = useRef((videoId && videoId !== 'undefined') ? videoId : (queryParams.get('videoId') || currentTransitionVideo || defaultFallback));

  const currentSongStartTimeRef = useRef(initialStartTime);
  const currentSongEndTimeRef = useRef(initialEndTime);
  const incomingVideoIdRef = useRef(null);
  const incomingStartTimeRef = useRef(0);
  const incomingEndTimeRef = useRef(0);

  useEffect(() => {
    if (currentPlaylistItem) {
      setCurrentPlaylistItem2(currentPlaylistItem);
      if (currentPlaylistItem.item?.startTime !== undefined && currentPlaylistItem.item?.startTime !== null) {
        currentSongStartTimeRef.current = Math.max(0, Number(currentPlaylistItem.item.startTime) || 0);
      }
      if (currentPlaylistItem.item?.endTime !== undefined && currentPlaylistItem.item?.endTime !== null) {
        currentSongEndTimeRef.current = Math.max(0, Number(currentPlaylistItem.item.endTime) || 0);
      }
    }
  }, [currentPlaylistItem]);

  useEffect(() => {
    if (currentIntroVideo) {
      setCurrentIntroVideo2(currentIntroVideo);
    }
  }, [currentIntroVideo]);

  useEffect(() => {
    if (currentTransitionVideo) {
      setCurrentTransitionVideo2(currentTransitionVideo);
    }
  }, [currentTransitionVideo]);

  useEffect(() => {
    if (!isIntegrated) {
      const handleResize = () => setHeight(window.innerHeight - 10);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    } else if (containerRef.current) {
      const updateH = () => {
        if (containerRef.current) {
          const h = containerRef.current.clientHeight;
          if (h > 50) setContainerHeight(h);
        }
      };
      updateH();
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.height > 50) {
            setContainerHeight(Math.floor(entry.contentRect.height));
          }
        }
      });
      ro.observe(containerRef.current);
      return () => ro.disconnect();
    }
  }, [isIntegrated]);

  const opts = useMemo(() => ({
    height: isIntegrated ? (containerHeight > 0 ? containerHeight : '100%') : height,
    width: '100%',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      controls: showControlsPlayer ? 1 : 0,
      autoplay: 1,
      enablejsapi: 1,
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      playsinline: 1,
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      iv_load_policy: 3,
      start: initialStartTime > 0 ? initialStartTime : 0,
    },
  }), [height, containerHeight, showControlsPlayer, isIntegrated, initialStartTime]);

  const playerRef = useRef(null);
  const playerRefA = useRef(null);
  const playerRefB = useRef(null);
  const [activeDeck, setActiveDeck] = useState('A');
  const activeDeckRef = useRef('A');
  activeDeckRef.current = activeDeck;
  const [transitionTargetDeck, setTransitionTargetDeck] = useState(null);

  const initialDeckAVideoIdRef = useRef(initialVideoId);
  const initialDeckBVideoIdRef = useRef(defaultFallback);
  const deckAVideoIdRef = useRef(initialVideoId);
  const deckBVideoIdRef = useRef(defaultFallback);
  const [deckAOpacity, setDeckAOpacity] = useState(1);
  const [deckBOpacity, setDeckBOpacity] = useState(0);
  const crossfadeFiredForVidRef = useRef(null);
  const midpointSyncedRef = useRef(false);
  const fadeTimerRef = useRef(null);

  const isDeck1InUse = !crossfadeEnabled || deckAOpacity > 0 || activeDeck === 'A' || transitionTargetDeck === 'A' || precuedDeckRef.current === 'A';
  const isDeck2InUse = crossfadeEnabled;

  const optsB = opts;

  const getActivePlayer = useCallback(() => {
    return activeDeckRef.current === 'A' ? playerRefA.current : playerRefB.current;
  }, []);

  const getInactivePlayer = useCallback(() => {
    return activeDeckRef.current === 'A' ? playerRefB.current : playerRefA.current;
  }, []);

  const [screenVolume, setScreenVolume] = useState(() => {
    const qVol = queryParams.get('volume');
    return qVol !== null ? parseInt(qVol, 10) : 100;
  });
  const screenVolumeRef = useRef(screenVolume);

  const [screenRate, setScreenRate] = useState(() => {
    const qRate = queryParams.get('rate');
    return qRate !== null ? parseFloat(qRate) : 1.0;
  });
  const screenRateRef = useRef(screenRate);

  const [screenPitch, setScreenPitch] = useState(() => {
    const qPitch = queryParams.get('pitch');
    return qPitch !== null ? parseInt(qPitch, 10) : 0;
  });

  var tar = null;

  useEffect(() => {
    if (!isIntegrated) {
      const handleResize = () => setHeight(window.innerHeight - 10);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isIntegrated]);

  // Desbloqueo de activación de usuario para Autoplay Policy de navegadores y keep-alive en background
  useEffect(() => {
    let silentAudioCtx = null;

    const initBackgroundKeepAlive = () => {
      try {
        if (!silentAudioCtx && (typeof window !== 'undefined') && (window.AudioContext || window.webkitAudioContext)) {
          const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
          silentAudioCtx = new AudioCtxClass();
          const osc = silentAudioCtx.createOscillator();
          const gain = silentAudioCtx.createGain();
          gain.gain.value = 0.00001;
          osc.connect(gain);
          gain.connect(silentAudioCtx.destination);
          osc.start();
        } else if (silentAudioCtx && silentAudioCtx.state === 'suspended') {
          silentAudioCtx.resume().catch(() => {});
        }
      } catch (e) {}
    };

    const handleResumeOrInteraction = () => {
      initBackgroundKeepAlive();
      const p = getActivePlayer() || playerRef.current;
      if (p && typeof p.getPlayerState === 'function') {
        try {
          const statePromise = p.getPlayerState();
          if (statePromise && typeof statePromise.then === 'function') {
            statePromise.then((st) => {
              // Si estaba en pausa (2), en cola esperando clic (5) o no iniciado (-1), reanudar
              if (st === 2 || st === 5 || st === -1) {
                p.playVideo();
              }
            }).catch(() => {});
          } else {
            const st = statePromise;
            if (st === 2 || st === 5 || st === -1) {
              p.playVideo();
            }
          }
        } catch (e) {}
      }
    };

    window.addEventListener('click', handleResumeOrInteraction);
    window.addEventListener('keydown', handleResumeOrInteraction);
    window.addEventListener('focus', handleResumeOrInteraction);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleResumeOrInteraction();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('click', handleResumeOrInteraction);
      window.removeEventListener('keydown', handleResumeOrInteraction);
      window.removeEventListener('focus', handleResumeOrInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (silentAudioCtx) {
        try { silentAudioCtx.close(); } catch (e) {}
      }
    };
  }, [getActivePlayer]);

  // Watchdog continuo para garantizar que la pista activa reproduzca sin requerir foco en la ventana del Player
  useEffect(() => {
    const autoPlayWatchdog = setInterval(() => {
      if (crossfadingRef.current) return;
      const p = getActivePlayer() || playerRef.current;
      if (p && typeof p.getPlayerState === 'function') {
        try {
          const st = p.getPlayerState();
          // Estados: 2 (PAUSED), 5 (CUED / en cola esperando interacción), -1 (UNSTARTED)
          if ((st === 2 || st === 5 || st === -1) && currentVideoIdRef.current) {
            console.log('[Autoplay Watchdog] Player inactivo en background (estado:', st, ') - forzando playVideo() sin foco');
            if (typeof p.unMute === 'function') p.unMute();
            if (typeof p.playVideo === 'function') p.playVideo();
          }
        } catch (e) {}
      }
    }, 1500);

    return () => clearInterval(autoPlayWatchdog);
  }, [getActivePlayer]);

  const handleCloseModal = () => {
    setOpenModal(false);
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'overlayClosed' }, '*');
    }
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const bc = new BroadcastChannel('garaoke_player_sync');
        bc.postMessage({ type: 'overlayClosed' });
        bc.close();
      }
    } catch (e) {}
  };

  const notifyHost = useCallback((payload) => {
    if (window.opener && !window.opener.closed) {
      try { window.opener.postMessage(payload, '*'); } catch (e) {}
    }
    if (window.parent && window.parent !== window) {
      try { window.parent.postMessage(payload, '*'); } catch (e) {}
    }
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const bc = new BroadcastChannel('garaoke_player_sync');
        bc.postMessage(payload);
        bc.close();
      }
    } catch (e) {}
  }, []);

  const playSong = useCallback((vidId, startTime = 0, endTime = 0) => {
    if (!vidId) return;
    const startSec = Math.max(0, Number(startTime) || 0);
    const endSec = Math.max(0, Number(endTime) || 0);
    const lastVid = currentVideoIdRef.current;

    // Si el video ya es el que está reproduciéndose y no estamos en crossfade, ignorar para evitar recarga innecesaria
    if (currentVideoIdRef.current === vidId && !crossfadingRef.current) {
      return;
    }

    currentVideoIdRef.current = vidId;
    currentSongStartTimeRef.current = startSec;
    currentSongEndTimeRef.current = endSec;
    incomingVideoIdRef.current = null;
    incomingStartTimeRef.current = 0;
    incomingEndTimeRef.current = 0;

    if (typeof setVideoId === 'function') {
      setVideoId(vidId);
    }
    crossfadingRef.current = false;
    setTransitionTargetDeck(null);
    crossfadeFiredForVidRef.current = null;
    midpointSyncedRef.current = false;
    isEndOfPlaylistRef.current = false;
    precuedVideoIdRef.current = null;
    precuedStartSecRef.current = 0;
    precuedDeckRef.current = null;

    // Notificar al host y a la consola para marcar la anterior como reproducida y activar la nueva
    const isSpecial = vidId === currentTransitionVideo2 || vidId === currentIntroVideo2;
    if (!isSpecial) {
      notifyHost({
        type: 'changeVideo',
        videoId: vidId,
        previousVideoId: isSpecial ? null : (lastVid && lastVid !== vidId ? lastVid : null),
        startTime: startSec,
        endTime: endSec,
      });
    }

    // Determinar cuál es el deck activo actual (puede ser A o B tras un crossfade previo)
    let currentActive = activeDeckRef.current;
    if (!crossfadeEnabled) {
      currentActive = 'A';
      setActiveDeck('A');
      activeDeckRef.current = 'A';
      setTransitionTargetDeck(null);
    }
    const inactivePlayerRef = currentActive === 'A' ? playerRefB : playerRefA;

    // Detener y resetear el deck INACTIVO para que nunca quede ningún video reproduciéndose en segundo plano
    if (inactivePlayerRef.current) {
      try {
        if (typeof inactivePlayerRef.current.setVolume === 'function') inactivePlayerRef.current.setVolume(0);
        if (typeof inactivePlayerRef.current.pauseVideo === 'function') inactivePlayerRef.current.pauseVideo();
        if (typeof inactivePlayerRef.current.stopVideo === 'function') inactivePlayerRef.current.stopVideo();
      } catch (e) {}
    }

    // Limpiar el deck inactivo y asegurar opacidades correctas
    if (currentActive === 'A') {
      deckAVideoIdRef.current = vidId;
      setDeckBOpacity(0);
      setDeckAOpacity(1);
    } else {
      deckBVideoIdRef.current = vidId;
      setDeckAOpacity(0);
      setDeckBOpacity(1);
    }

    // Mantener el deck activo actual (respetando si tras un crossfade es B)
    playerRef.current = currentActive === 'A' ? playerRefA.current : playerRefB.current;

    const player = playerRef.current;
    if (player) {
      try {
        if (typeof player.setVolume === 'function' && screenVolumeRef.current !== undefined) {
          player.setVolume(screenVolumeRef.current);
        }
        if (typeof player.unMute === 'function') {
          player.unMute();
        }
        if (typeof player.setPlaybackRate === 'function' && screenRateRef.current !== undefined) {
          player.setPlaybackRate(screenRateRef.current);
        }

        // Cargar video instantáneamente en el iframe existente sin destruirlo ni perder la activación de audio
        if (typeof player.loadVideoById === 'function') {
          player.loadVideoById({
            videoId: vidId,
            startSeconds: startSec,
          });

          if (typeof player.playVideo === 'function') {
            player.playVideo();
          }
          if (startSec > 0 && typeof player.seekTo === 'function') {
            setTimeout(() => {
              try { player.seekTo(startSec, true); } catch (e) {}
            }, 250);
          }
        } else if (typeof player.playVideo === 'function') {
          player.playVideo();
        }
      } catch (err) {
        console.warn('Error en playSong:', err);
      }
    }
  }, [setVideoId, currentTransitionVideo2, currentIntroVideo2, notifyHost]);

  useEffect(() => {
    if (crossfadingRef.current) return;
    // Si el video ya está reproduciéndose por crossfade o por carga directa, no recargar
    if (incomingVideoIdRef.current === videoId || currentVideoIdRef.current === videoId) return;
    if (isIntegrated && videoId && videoId !== currentVideoIdRef.current) {
      const itemStart = Math.max(0, Number(currentPlaylistItem2?.item?.startTime ?? currentPlaylistItem2?.item?.t ?? 0) || 0);
      const itemEnd = Math.max(0, Number(currentPlaylistItem2?.item?.endTime ?? 0) || 0);
      playSong(videoId, itemStart, itemEnd);
    }
  }, [isIntegrated, videoId, playSong, currentPlaylistItem2]);

  // Notificar cierre de ventana flotante inmediatamente al host
  useEffect(() => {
    if (isIntegrated) return;
    const handleBeforeUnload = () => {
      notifyHost({ type: 'playerClosed' });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isIntegrated, notifyHost]);

  // 🛑 [AI CRITICAL GUARD - DO NOT MODIFY SCOPE]:
  // En Player.jsx NO existen variables globales ni props 'playlist' o 'presetPlaylists'.
  // Para consultar la lista de reproducción activa SIEMPRE se debe usar getActivePlaylistSnapshot().
  // NUNCA agregar 'playlist' o 'presetPlaylists' al array de dependencias ni en el scope local de Player.jsx.
  const syncPlaybackState = useCallback((status = 'PLAYING', explicitTime = null, explicitVidId = null) => {
    const p = getActivePlayer() || playerRef.current;
    const vidId = explicitVidId !== undefined ? explicitVidId : (p?.playerInfo?.videoData?.video_id || currentVideoIdRef.current || videoId);
    
    // Si es intro o transición, no se considera canción karaoke para los móviles
    const isSpecialVideo = vidId === currentTransitionVideo2 || vidId === currentIntroVideo2 || !vidId;
    const currentSongVidId = isSpecialVideo ? null : vidId;

    let time = 0;
    if (typeof explicitTime === 'number') {
      time = explicitTime;
    } else if (p && typeof p.getCurrentTime === 'function') {
      try {
        time = p.getCurrentTime();
      } catch (err) {}
    }

    let duration = 0;
    if (p && typeof p.getDuration === 'function') {
      try {
        duration = p.getDuration();
      } catch (err) {}
    }

    // Resolución exhaustiva del cantante asignado al track activo
    let assigned = '';
    if (currentPlaylistItem2) {
      assigned = currentPlaylistItem2.item?.assignedTo || currentPlaylistItem2.assignedTo || '';
    }
    if (!assigned && currentSongVidId) {
      const snap = getActivePlaylistSnapshot();
      if (snap && Array.isArray(snap.items)) {
        const match = snap.items.find(it => (it.videoId === currentSongVidId || it.id === currentSongVidId || it.item?.videoId === currentSongVidId));
        if (match) assigned = match.assignedTo || match.item?.assignedTo || match.singer || match.performer || '';
      }
    }

    const title = p?.playerInfo?.videoData?.title || (currentPlaylistItem2 && currentPlaylistItem2.item ? currentPlaylistItem2.item.title : (currentPlaylistItem2?.title || ''));
    const startTimeVal = currentSongStartTimeRef.current || 0;
    const endTimeVal = currentSongEndTimeRef.current || 0;

    const statePayload = {
      videoId: currentSongVidId,
      status: status,
      baseTime: Math.max(0, Math.floor(time)),
      startTime: startTimeVal,
      endTime: endTimeVal,
      startedAt: Date.now(),
      playbackRate: screenRateRef.current || 1.0,
      duration: Math.floor(duration),
      assignedTo: assigned || '',
      title: title || '',
      playlist: currentPlaylistItem2?.playlist || '',
    };

    // 1. Guardar en Firestore para clientes móviles y respaldo
    try {
      updateUserField('currentPlaybackState', JSON.stringify(statePayload), effectiveHostId);
      if (currentSongVidId) {
        updateUserField('currentVideoTime', Math.floor(time), effectiveHostId);
        updateUserField('currentVideoData', JSON.stringify({
          videoId: currentSongVidId,
          time: Math.floor(time),
          startTime: startTimeVal,
          endTime: endTimeVal,
          assignedTo: assigned || '',
          title: title || ''
        }), effectiveHostId);
      }
    } catch (err) {
      console.warn('Error al actualizar currentPlaybackState en Firestore:', err);
    }

    // 2. Notificar al backend Express SSE/REST si está activo (red local o Render)
    const syncBackendEnabled = isSyncBackendAvailable;
    if (syncBackendEnabled && effectiveHostId) {
      try {
        fetch(`${apiBaseUrl}/sync/state/${effectiveHostId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(statePayload),
        }).catch(() => {});
      } catch (err) {}
    }

    // 3. Sincronizar estado local videoId si se proveyó la función
    if (typeof setVideoId === 'function' && currentSongVidId) {
      setVideoId(currentSongVidId);
    }
  }, [getActivePlayer, effectiveHostId, currentTransitionVideo2, currentIntroVideo2, currentPlaylistItem2, videoId, updateUserField, setVideoId]);

  const onEndVideo = useCallback((deck = 'A') => {
    // Si terminó un deck inactivo o mientras se está en pleno crossfade, ignorar
    if (crossfadingRef.current) return;
    if (crossfadeEnabled && deck !== activeDeckRef.current) return;

    const endVid = currentVideoIdRef.current || videoId;
    const isSpecial = endVid === currentTransitionVideo2 || endVid === currentIntroVideo2;

    // Notificar siempre al host que la canción terminó para que se marque played: true
    if (!isSpecial && endVid) {
      notifyHost({ type: 'onSongEnded', videoId: endVid, previousVideoId: endVid });
    }

    if (continuousPlay && !isSpecial) {
      if (typeof onPlayNextSong === 'function') {
        onPlayNextSong(endVid, false);
      }
    } else {
      playSong(currentTransitionVideo2);
      syncPlaybackState('STOPPED', 0, null);
      updateUserField(
        'currentVideoData',
        JSON.stringify(
          {
            videoId: null, 
            time: 0, 
            assignedTo: null
          }
        ),
        effectiveHostId
      );
    }
  }, [crossfadeEnabled, continuousPlay, currentTransitionVideo2, currentIntroVideo2, videoId, onPlayNextSong, notifyHost, playSong, syncPlaybackState, effectiveHostId, updateUserField]);

  const onPlayVideo = (e, deck = 'A') => {
    if (e?.target) {
      tar = e.target;
      if (deck === 'A') playerRefA.current = e.target;
      if (deck === 'B') playerRefB.current = e.target;
      if (activeDeckRef.current === deck) playerRef.current = e.target;

      // Si este evento pertenece a un deck que NO es el activo y NO estamos en crossfade, apagarlo de inmediato
      if (activeDeckRef.current !== deck) {
        if (!crossfadingRef.current) {
          try {
            if (typeof e.target.setVolume === 'function') e.target.setVolume(0);
            if (typeof e.target.pauseVideo === 'function') e.target.pauseVideo();
          } catch (err) {}
        }
        return;
      }

      try {
        if (typeof e.target.setVolume === 'function' && screenVolumeRef.current !== undefined) {
          // Solo aplicar volumen maestro si no está atenuado por crossfade
          if (!crossfadingRef.current || activeDeckRef.current === deck) {
            e.target.setVolume(screenVolumeRef.current);
          }
        }
        if (typeof e.target.setPlaybackRate === 'function' && screenRateRef.current !== undefined) {
          e.target.setPlaybackRate(screenRateRef.current);
        }
      } catch (err) {}

      // Verificación de seguridad de startTime si el video activo comenzó en 0 indebidamente
      if (currentSongStartTimeRef.current > 0 && typeof e.target.getCurrentTime === 'function' && typeof e.target.seekTo === 'function') {
        try {
          const curTime = e.target.getCurrentTime();
          const targetStart = currentSongStartTimeRef.current;
          if (curTime < targetStart - 1.5) {
            e.target.seekTo(targetStart, true);
          }
        } catch (seekErr) {}
      }

      const video_id = e.target.playerInfo?.videoData?.video_id || currentVideoIdRef.current;
      if (video_id && activeDeckRef.current === deck && !crossfadingRef.current) {
        currentVideoIdRef.current = video_id;
        if (video_id === currentTransitionVideo2 || video_id === currentIntroVideo2) {
          if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
          }
          syncPlaybackState('STOPPED', 0, null);
        } else {
          if (video_id !== videoId) {
            setVideoId(video_id);
          }
          // Emitir estado PLAYING inmediato
          syncPlaybackState('PLAYING');
        }
      }
    }
  };

  const onPauseVideo = (e, deck = 'A') => {
    if (activeDeckRef.current === deck && !crossfadingRef.current) {
      syncPlaybackState('PAUSED');
    }
  };

  const getCurrentTime = () => {
    const p = getActivePlayer() || playerRef.current;
    if (p && typeof p.getCurrentTime === 'function') {
      try {
        const time = p.getCurrentTime();
        const vid = p.playerInfo?.videoData?.video_id || currentVideoIdRef.current;
        // Emite heartbeat con posición real y videoId para sincronización directa del Monitor
        updateUserField('currentVideoTime', Math.floor(time), effectiveHostId);
        if (vid) {
          updateUserField('currentVideoId', vid, effectiveHostId);
        }
        console.debug('Heartbeat sincronización video:', vid, ' time=', Math.floor(time));
      } catch (err) {}
    } 
  };

  const lastHostTimeRef = useRef(0);
  const lastTrackVidRef = useRef(null);

  // Emitir progreso continuo de reproducción para el indicador circular en Playlist y Búsqueda,
  // cortar si se alcanza endTime, y detectar en tiempo real saltos manuales del anfitrión
  useEffect(() => {
    const progressTimer = setInterval(() => {
      // Durante crossfade, si aún no cruzamos el punto medio, monitorear el deck saliente;
      // si ya cruzamos el punto medio, monitorear el deck entrante.
      let p;
      if (crossfadingRef.current) {
        if (!midpointSyncedRef.current) {
          p = activeDeckRef.current === 'A' ? playerRefA.current : playerRefB.current;
        } else {
          p = activeDeckRef.current === 'A' ? playerRefB.current : playerRefA.current;
        }
      } else {
        p = getActivePlayer() || playerRef.current;
      }
      if (p && typeof p.getCurrentTime === 'function') {
        try {
          const time = p.getCurrentTime();
          let dur = 0;
          if (typeof p.getDuration === 'function') {
            dur = p.getDuration() || 0;
          }
          const vid = crossfadingRef.current
            ? (midpointSyncedRef.current ? incomingVideoIdRef.current : (currentVideoIdRef.current || (activeDeckRef.current === 'A' ? deckAVideoIdRef.current : deckBVideoIdRef.current)))
            : (p.playerInfo?.videoData?.video_id || currentVideoIdRef.current);
          if (vid && vid !== currentTransitionVideo2 && vid !== currentIntroVideo2) {
            // Chequeo de corte por endTime configurado (cuando no hay crossfade activo)
            const endLimit = crossfadingRef.current
              ? (midpointSyncedRef.current ? incomingEndTimeRef.current : currentSongEndTimeRef.current)
              : (currentSongEndTimeRef.current || 0);

            const startLimit = crossfadingRef.current
              ? (midpointSyncedRef.current ? incomingStartTimeRef.current : currentSongStartTimeRef.current)
              : (currentSongStartTimeRef.current || 0);

            if (endLimit > 0 && time >= endLimit && !crossfadingRef.current) {
              console.log(`[Player] Fin de pista alcanzado por endTime (${endLimit}s) para video ${vid}. Ejecutando onEndVideo...`);
              onEndVideo(activeDeckRef.current);
              return;
            }

            const effectiveEnd = (endLimit > 0) ? Math.min(dur > 0 ? dur : endLimit, endLimit) : dur;
            const trackSpan = effectiveEnd - startLimit;

            const progress = (trackSpan > 0 && time >= startLimit)
              ? Math.min(1, Math.max(0, (time - startLimit) / trackSpan))
              : (dur > 0 && time >= 0 ? Math.min(1, Math.max(0, time / dur)) : 0);

            if (dur > 0) {
              try {
                const cached = JSON.parse(localStorage.getItem('garaoke_duration_cache') || '{}');
                if (!cached[vid]) {
                  cached[vid] = Math.round(dur);
                  localStorage.setItem('garaoke_duration_cache', JSON.stringify(cached));
                }
              } catch (e) {}
            }

            // Detección de saltos manuales del anfitrión (omitiendo durante crossfade o cambios de pista)
            if (lastTrackVidRef.current !== vid || crossfadingRef.current) {
              lastTrackVidRef.current = vid;
              lastHostTimeRef.current = time;
            } else {
              if (lastHostTimeRef.current > 0 && Math.abs(time - (lastHostTimeRef.current + 0.35)) > 2.5) {
                console.log(`[Player Host Seek] Salto manual detectado en anfitrión: ${lastHostTimeRef.current.toFixed(1)}s -> ${time.toFixed(1)}s. Sincronizando inmediatamente con Monitores y Cloud...`);
                syncPlaybackState('PLAYING', time, vid);
              }
              lastHostTimeRef.current = time;
            }

            const payload = {
              videoId: vid,
              currentTime: time,
              duration: dur,
              effectiveDuration: effectiveEnd,
              startTime: startLimit,
              endTime: endLimit,
              progress: progress,
            };

            window.dispatchEvent(new CustomEvent('garaoke_playback_progress', { detail: payload }));

            try {
              if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
                const bc = new BroadcastChannel('garaoke_player_sync');
                bc.postMessage({ type: 'playbackProgress', ...payload });
                bc.close();
              }
            } catch (e) {}
          }
        } catch (err) {}
      }
    }, 350);

    return () => clearInterval(progressTimer);
  }, [getActivePlayer, currentTransitionVideo2, currentIntroVideo2, syncPlaybackState, onEndVideo]);

  const setTheInterval = () => {
    if (intervalId) clearInterval(intervalId);
    const heartbeatSec = parseInt(import.meta.env.VITE_SYNC_HEARTBEAT_SEC, 10) || 5;
    const newIntervalId = setInterval(getCurrentTime, heartbeatSec * 1000);
    setIntervalId(newIntervalId);
  };

  const resetAndStartDeck = useCallback((player, vidId, startSec = 0) => {
    if (!player) return;
    try {
      if (typeof player.stopVideo === 'function') {
        player.stopVideo();
      }
    } catch (e) {}

    // Pequeño timeout de 50ms para que el iframe de YouTube procese la detención antes de inicializar la carga
    setTimeout(() => {
      try {
        if (typeof player.unMute === 'function') {
          player.unMute();
        }
        if (typeof player.setVolume === 'function') {
          player.setVolume(0);
        }
        if (typeof player.setPlaybackRate === 'function' && screenRateRef.current !== undefined) {
          player.setPlaybackRate(screenRateRef.current);
        }
        if (typeof player.loadVideoById === 'function') {
          player.loadVideoById({
            videoId: vidId,
            startSeconds: startSec,
          });
        }
        if (typeof player.playVideo === 'function') {
          player.playVideo();
        }
        if (startSec > 0 && typeof player.seekTo === 'function') {
          setTimeout(() => {
            try { player.seekTo(startSec, true); } catch (e) {}
          }, 200);
        }
      } catch (err) {
        console.warn('Error en resetAndStartDeck:', err);
      }
    }, 50);
  }, []);

  const onStateChangeVideo = (event, deck = 'A') => {
    // Si este evento pertenece al deck entrante durante el crossfade o al deck activo
    const isIncomingDeck = crossfadingRef.current && (
      (activeDeckRef.current === 'A' && deck === 'B') ||
      (activeDeckRef.current === 'B' && deck === 'A')
    );
    const isActiveDeck = activeDeckRef.current === deck;

    // Si NO pertenece ni al activo ni al entrante, ignorar completamente para no revivir decks salientes finalizados
    if (!isActiveDeck && !isIncomingDeck) return;

    // 5 = CUED (video cargado en cola esperando acción)
    // 2 = PAUSED (si el reproductor quedó en pausa por bloqueo de autoplay del navegador)
    // -1 = UNSTARTED (video cargado pero aún no arrancó)
    // 3 = BUFFERING
    if (event?.data === 5 || event?.data === 2 || event?.data === -1 || (isIncomingDeck && event?.data === 3)) {
      try {
        if (event.target && typeof event.target.playVideo === 'function') {
          if (isIncomingDeck && typeof event.target.unMute === 'function') {
            event.target.unMute();
          }
          event.target.playVideo();
        }
      } catch (err) {}
    }
  };

  const onErrorVideo = (error, deck = 'A') => {
    // Si el error ocurrió en el deck inactivo, ignorarlo completamente para no interrumpir la reproducción activa
    if (deck !== activeDeckRef.current) {
      console.warn(`[Player] Error en deck inactivo (${deck}):`, error?.data);
      return;
    }
    console.warn('Error en reproducción de video de YouTube (Player deck ' + deck + '):', error?.data);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    const endVid = currentVideoIdRef.current || videoId;
    const isSpecial = endVid === currentTransitionVideo2 || endVid === currentIntroVideo2;

    // Errores de YouTube: 100 (no encontrado), 101/150 (bloqueado embebido), 2 (param inválido), 5 (error HTML5)
    if (continuousPlay && !isSpecial) {
      if (typeof onPlayNextSong === 'function') {
        onPlayNextSong(endVid, true);
      } else {
        notifyHost({ type: 'onSongError', videoId: endVid, errorCode: error?.data });
      }
    }
  };

  const onReadyVideo = (event, deck = 'A') => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (event.target) {
      tar = event.target;
      if (deck === 'A') playerRefA.current = event.target;
      if (deck === 'B') playerRefB.current = event.target;
      if (activeDeckRef.current === deck) playerRef.current = event.target;

      // Asegurar permisos de autoplay y fullscreen en el iframe de YouTube de forma segura
      if (typeof event.target.getIframe === 'function') {
        try {
          const iframe = event.target.getIframe();
          if (iframe && typeof iframe.then === 'function') {
            iframe.then((el) => {
              if (el && typeof el.setAttribute === 'function') {
                el.setAttribute('allow', 'accelerometer; autoplay *; clipboard-write; encrypted-media *; gyroscope; picture-in-picture; web-share');
                el.setAttribute('allowfullscreen', 'true');
              }
            }).catch(() => {});
          } else if (iframe && typeof iframe.setAttribute === 'function') {
            iframe.setAttribute('allow', 'accelerometer; autoplay *; clipboard-write; encrypted-media *; gyroscope; picture-in-picture; web-share');
            iframe.setAttribute('allowfullscreen', 'true');
          }
        } catch (e) {}
      }

      // Si este evento pertenece al deck entrante durante pleno crossfade, arrancar en silencio en el segundo correspondiente
      if (crossfadingRef.current && activeDeckRef.current !== deck) {
        try {
          if (typeof event.target.unMute === 'function') event.target.unMute();
          if (typeof event.target.setVolume === 'function') event.target.setVolume(0);
          if (incomingStartTimeRef.current > 0 && typeof event.target.seekTo === 'function') {
            event.target.seekTo(incomingStartTimeRef.current, true);
          }
          if (typeof event.target.playVideo === 'function') {
            event.target.playVideo();
          }
        } catch (e) {}
        return;
      }

      // Si el deck no es el activo inicialmente, mantener volumen en 0
      const isCurrentActive = activeDeckRef.current === deck;
      try {
        if (typeof event.target.setVolume === 'function') {
          event.target.setVolume(isCurrentActive ? (screenVolumeRef.current !== undefined ? screenVolumeRef.current : 100) : 0);
        }
        if (typeof event.target.setPlaybackRate === 'function' && screenRateRef.current !== undefined) {
          event.target.setPlaybackRate(screenRateRef.current);
        }
        
        if (isCurrentActive) {
          const targetVid = currentVideoIdRef.current || initialVideoId;
          const isTransitionOrIntro = targetVid === currentTransitionVideo2 || targetVid === currentIntroVideo2;
          const startSec = isTransitionOrIntro ? 0 : (currentSongStartTimeRef.current || initialStartTime || 0);
          if (targetVid && targetVid !== initialDeckAVideoIdRef.current && typeof event.target.loadVideoById === 'function') {
            event.target.loadVideoById({ videoId: targetVid, startSeconds: startSec });
          }
          if (typeof event.target.playVideo === 'function') {
            event.target.playVideo();
          }
          if (startSec > 0 && typeof event.target.seekTo === 'function') {
            setTimeout(() => {
              try { event.target.seekTo(startSec, true); } catch (e) {}
            }, 250);
          }
        } else {
          try {
            if (typeof event.target.setVolume === 'function') event.target.setVolume(0);
            if (typeof event.target.pauseVideo === 'function') event.target.pauseVideo();
            if (typeof event.target.stopVideo === 'function') event.target.stopVideo();
          } catch (e) {}

          if (crossfadeEnabled && !crossfadingRef.current && nextSongRef.current?.videoId && precuedVideoIdRef.current !== nextSongRef.current.videoId) {
            const nextVid = nextSongRef.current.videoId;
            const precueStart = Math.max(0, Number(nextSongRef.current.startTime ?? nextSongRef.current.item?.startTime ?? nextSongRef.current.item?.t ?? 0) || 0);
            if (typeof event.target.cueVideoById === 'function') {
              console.log(`🎛️ [Pre-cueing onReady] Pre-cargando ${nextVid} en Deck ${deck} (start: ${precueStart}s)...`);
              try {
                event.target.cueVideoById({ videoId: nextVid, startSeconds: precueStart });
                precuedVideoIdRef.current = nextVid;
                precuedStartSecRef.current = precueStart;
                precuedDeckRef.current = deck;
              } catch (cueErr) {
                console.warn('Error en pre-cueing onReady:', cueErr);
              }
            }
          }
        }
      } catch (err) {
        console.warn('Error en onReadyVideo:', err);
      }

      if (isCurrentActive) {
        const targetSongId = currentVideoIdRef.current || initialVideoId;
        const isTransitionOrIntro = targetSongId === currentTransitionVideo2 || targetSongId === currentIntroVideo2;

        if (targetSongId && !isTransitionOrIntro) {
          if (allowInteractivity) {
            const snap = getActivePlaylistSnapshot();
            const resolvedAssigned = (currentPlaylistItem2?.item?.assignedTo || currentPlaylistItem2?.assignedTo || '').trim() ||
              (snap && Array.isArray(snap.items) ? (snap.items.find(it => it.videoId === targetSongId || it.id === targetSongId || it.item?.videoId === targetSongId)?.assignedTo || '') : '') || '';
            const currentVideoData = {
              videoId: targetSongId,
              assignedTo: resolvedAssigned,
              votes: currentPlaylistItem2?.item?.votes || currentPlaylistItem2?.votes || [],
              playlist: currentPlaylistItem2?.playlist || ''
            };
            setTimeout(() => {
              updateUserField(
                'currentVideoData',
                JSON.stringify(currentVideoData),
                effectiveHostId
              );
              // Emitir con el startTime real (no 0) para que los monitores sincronicen correctamente
              const realStartSec = currentSongStartTimeRef.current || 0;
              syncPlaybackState('PLAYING', realStartSec, targetSongId);
              // Emitir heartbeat inmediato con videoId para que el Monitor sepa a qué video corresponde
              updateUserField('currentVideoTime', Math.floor(realStartSec), effectiveHostId);
              updateUserField('currentVideoId', targetSongId, effectiveHostId);
            }, 1000);
            setTimeout(setTheInterval, 2000);
          }
        } else if (isTransitionOrIntro) {
          console.log('Begin play transition or intro');
          const currentVideoData = {
            videoId: null,
            assignedTo: '',
            votes: [],
            playlist: ''
          };
          setTimeout(() => {
            updateUserField(
              'currentVideoData',
              JSON.stringify(currentVideoData),
              effectiveHostId
            );
            syncPlaybackState('STOPPED', 0, null);
          }, 1000);
        }
      }

      // Si este es el deck entrante durante un crossfade, asegurar que inicie la reproducción
      if (crossfadingRef.current && incomingVideoIdRef.current) {
        try {
          if (typeof event.target.unMute === 'function') event.target.unMute();
          if (typeof event.target.setVolume === 'function') event.target.setVolume(0);
          if (typeof event.target.playVideo === 'function') event.target.playVideo();
        } catch (e) {}
      }
    }
  };

  const startCrossfade = useCallback((incomingVid, nextSongData, explicitTargetDeck = null, explicitDuration = null, explicitStartSec = null, explicitEndSec = null) => {
    if (crossfadingRef.current || !incomingVid) return;
    if (fadeTimerRef.current) {
      clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    crossfadingRef.current = true;
    midpointSyncedRef.current = false;
    incomingVideoIdRef.current = incomingVid;

    const incomingStartSec = explicitStartSec !== null && !isNaN(Number(explicitStartSec))
      ? Math.max(0, Number(explicitStartSec))
      : Math.max(0, Number(nextSongData?.startTime ?? nextSongData?.item?.startTime ?? nextSongData?.item?.t ?? 0) || 0);

    const incomingEndSec = explicitEndSec !== null && !isNaN(Number(explicitEndSec))
      ? Math.max(0, Number(explicitEndSec))
      : Math.max(0, Number(nextSongData?.endTime ?? nextSongData?.item?.endTime ?? 0) || 0);

    incomingStartTimeRef.current = incomingStartSec;
    incomingEndTimeRef.current = incomingEndSec;

    // Deck entrante comandado por la consola o alternancia respecto al activo
    const targetDeck = explicitTargetDeck || (activeDeckRef.current === 'A' ? 'B' : 'A');
    const isIncomingDeckB = targetDeck === 'B';

    const outgoingVid = currentVideoIdRef.current || (isIncomingDeckB ? deckAVideoId : deckBVideoId);
    const outgoingPlayer = isIncomingDeckB ? playerRefA.current : playerRefB.current;
    const incomingPlayer = isIncomingDeckB ? playerRefB.current : playerRefA.current;

    notifyHost({
      type: 'START_CROSSFADE',
      incomingVid,
      outgoingVid,
      activeDeck: targetDeck,
    });

    console.log(`🎛️ [Player] Iniciando Crossfade físico hacia Deck ${targetDeck} con video ${incomingVid} (Start: ${incomingStartSec}s, End: ${incomingEndSec}s)...`);

    // Blanquear y preparar el estado del Deck entrante
    setTransitionTargetDeck(targetDeck);
    if (isIncomingDeckB) {
      deckBVideoIdRef.current = incomingVid;
      setDeckBOpacity(0);
      setDeckAOpacity(1);
    } else {
      deckAVideoIdRef.current = incomingVid;
      setDeckAOpacity(0);
      setDeckBOpacity(1);
    }

    // Iniciar y desbloquear el deck entrante
    const isPrecueHit = precuedVideoIdRef.current === incomingVid && precuedDeckRef.current === targetDeck && incomingPlayer;
    if (isPrecueHit) {
      console.log(`🎛️ [Pre-cue HIT] Deck ${targetDeck} ya tiene ${incomingVid} pre-cargado. Arrancando instantáneamente.`);
      try {
        if (typeof incomingPlayer.setVolume === 'function') {
          incomingPlayer.setVolume(0);
        }
        if (typeof incomingPlayer.unMute === 'function') {
          incomingPlayer.unMute();
        }
        if (typeof incomingPlayer.setPlaybackRate === 'function' && screenRateRef.current !== undefined) {
          incomingPlayer.setPlaybackRate(screenRateRef.current);
        }
        if (typeof incomingPlayer.playVideo === 'function') {
          incomingPlayer.playVideo();
        }
        if (incomingStartSec > 0 && typeof incomingPlayer.seekTo === 'function') {
          setTimeout(() => {
            try { incomingPlayer.seekTo(incomingStartSec, true); } catch (e) {}
          }, 80);
        }
      } catch (hitErr) {
        console.warn('Error al activar deck pre-cargado:', hitErr);
      }
      precuedVideoIdRef.current = null;
      precuedDeckRef.current = null;
    } else if (incomingPlayer) {
      console.log(`🎛️ [Pre-cue MISS] Deck ${targetDeck} no tiene pre-cargado ${incomingVid}. Cargando en caliente...`);
      resetAndStartDeck(incomingPlayer, incomingVid, incomingStartSec);
    }

    const durationSec = explicitDuration || crossfadeDuration || 5;
    const durationMs = Math.max(1000, durationSec * 1000);
    const intervalStepMs = 100;
    let elapsedMs = 0;
    const masterVol = screenVolumeRef.current !== undefined ? screenVolumeRef.current : 100;

    const fadeTimer = setInterval(() => {
      elapsedMs += intervalStepMs;
      const progress = Math.min(1, elapsedMs / durationMs);

      const outgoingVol = Math.round((1 - progress) * masterVol);
      const incomingVol = Math.round(progress * masterVol);
      const outgoingOpacity = Math.max(0, 1 - progress);
      const incomingOpacity = Math.min(1, progress);

      if (isIncomingDeckB) {
        setDeckAOpacity(outgoingOpacity);
        setDeckBOpacity(incomingOpacity);
      } else {
        setDeckBOpacity(outgoingOpacity);
        setDeckAOpacity(incomingOpacity);
      }

      const outP = isIncomingDeckB ? playerRefA.current : playerRefB.current;
      const inP = isIncomingDeckB ? playerRefB.current : playerRefA.current;

      try {
        if (outP && typeof outP.setVolume === 'function') {
          outP.setVolume(outgoingVol);
        }
        if (inP && typeof inP.setVolume === 'function') {
          inP.setVolume(incomingVol);
          if (incomingVol > 0 && typeof inP.unMute === 'function') {
            inP.unMute();
          }
          // Watchdog en cada tick: si el deck entrante está pausado o en cola, forzar play
          if (typeof inP.getPlayerState === 'function') {
            try {
              const inState = inP.getPlayerState();
              if (inState === 2 || inState === 5 || inState === -1 || inState === 3) {
                if (typeof inP.playVideo === 'function') {
                  inP.playVideo();
                }
              }
            } catch (stErr) {}
          }
        }
      } catch (e) {}

      // Punto medio (t >= 0.5): Notificar cambio al monitor y al host para sincronización perfecta
      if (progress >= 0.5 && !midpointSyncedRef.current) {
        midpointSyncedRef.current = true;
        currentVideoIdRef.current = incomingVid;
        currentSongStartTimeRef.current = incomingStartSec;
        currentSongEndTimeRef.current = incomingEndSec;
        const isSpecial = incomingVid === currentTransitionVideo2 || incomingVid === currentIntroVideo2;
        const inP = isIncomingDeckB ? playerRefB.current : playerRefA.current;
        let incomingCurrentTime = incomingStartSec;
        if (inP && typeof inP.getCurrentTime === 'function') {
          try { incomingCurrentTime = Math.max(incomingStartSec, Math.floor(inP.getCurrentTime())); } catch (e) {}
        }
        if (nextSongData) {
          setCurrentPlaylistItem2({
            playlist: nextSongData.playlist || '',
            item: {
              videoId: incomingVid,
              title: nextSongData.title || '',
              assignedTo: nextSongData.assignedTo || '',
              startTime: incomingStartSec,
              endTime: incomingEndSec,
              votes: []
            }
          });
        }
        if (isSpecial) {
          syncPlaybackState('STOPPED', 0, null);
        } else {
          syncPlaybackState('PLAYING', incomingCurrentTime, incomingVid);
          try {
            updateUserField('currentVideoTime', incomingCurrentTime, effectiveHostId);
            updateUserField('currentVideoId', incomingVid, effectiveHostId);
          } catch (e) {}
        }
        notifyHost({
          type: 'onMidpointCrossfade',
          videoId: incomingVid,
          previousVideoId: outgoingVid,
          nextSong: nextSongData,
          activeDeck: targetDeck,
        });
        if (typeof setVideoId === 'function') {
          setVideoId(incomingVid);
        }
      }

      // Al completar el fundido
      if (progress >= 1) {
        clearInterval(fadeTimer);
        fadeTimerRef.current = null;

        setActiveDeck(targetDeck);
        activeDeckRef.current = targetDeck;
        setTransitionTargetDeck(null);

        const finalActivePlayer = targetDeck === 'A' ? playerRefA.current : playerRefB.current;
        const finalInactivePlayer = targetDeck === 'A' ? playerRefB.current : playerRefA.current;
        playerRef.current = finalActivePlayer;

        // 1. Asegurar volumen al 100% y desmuteo del nuevo deck activo
        if (finalActivePlayer) {
          try {
            if (typeof finalActivePlayer.setVolume === 'function') {
              finalActivePlayer.setVolume(masterVol);
            }
            if (typeof finalActivePlayer.unMute === 'function') {
              finalActivePlayer.unMute();
            }
            if (typeof finalActivePlayer.playVideo === 'function') {
              finalActivePlayer.playVideo();
            }
          } catch (e) {}
        }

        // 2. Silenciar, frenar y limpiar el deck saliente para que NUNCA suene ni quede sucio
        const inactiveDeck = targetDeck === 'A' ? 'B' : 'A';
        if (finalInactivePlayer) {
          try {
            if (typeof finalInactivePlayer.setVolume === 'function') {
              finalInactivePlayer.setVolume(0);
            }
            if (precuedDeckRef.current !== inactiveDeck) {
              if (typeof finalInactivePlayer.pauseVideo === 'function') {
                finalInactivePlayer.pauseVideo();
              }
              if (typeof finalInactivePlayer.stopVideo === 'function') {
                finalInactivePlayer.stopVideo();
              }
            }
          } catch (e) {}
        }

        currentVideoIdRef.current = incomingVid;
        currentSongStartTimeRef.current = incomingStartSec;
        currentSongEndTimeRef.current = incomingEndSec;
        incomingVideoIdRef.current = null;
        incomingStartTimeRef.current = 0;
        incomingEndTimeRef.current = 0;
        crossfadeFiredForVidRef.current = null;
        if (typeof setVideoId === 'function') {
          setVideoId(incomingVid);
        }
        crossfadingRef.current = false;

        notifyHost({
          type: 'changeVideo',
          videoId: incomingVid,
          previousVideoId: outgoingVid,
          startTime: incomingStartSec,
          endTime: incomingEndSec,
          title: nextSongData?.title || '',
          assignedTo: nextSongData?.assignedTo || '',
          playlist: nextSongData?.playlist || '',
          nextSong: nextSongData,
          activeDeck: targetDeck,
          _fromCrossfade: true,
        });

        notifyHost({
          type: 'onDeckChanged',
          activeDeck: targetDeck,
          videoId: incomingVid,
        });

        // Pre-cueing proactivo del siguiente track en el deck que acaba de quedar libre
        setTimeout(() => {
          if (crossfadeEnabled && !crossfadingRef.current && nextSongRef.current?.videoId) {
            const nextVid = nextSongRef.current.videoId;
            const inactDeck = activeDeckRef.current === 'A' ? 'B' : 'A';
            const inactP = inactDeck === 'A' ? playerRefA.current : playerRefB.current;
            const precueStart = Math.max(0, Number(nextSongRef.current.startTime ?? nextSongRef.current.item?.startTime ?? nextSongRef.current.item?.t ?? 0) || 0);
            if (inactP && typeof inactP.cueVideoById === 'function' && precuedVideoIdRef.current !== nextVid) {
              console.log(`🎛️ [Pre-cueing post-crossfade] Pre-cargando ${nextVid} en Deck ${inactDeck} (start: ${precueStart}s)...`);
              try {
                if (typeof inactP.setVolume === 'function') inactP.setVolume(0);
                inactP.cueVideoById({ videoId: nextVid, startSeconds: precueStart });
                precuedVideoIdRef.current = nextVid;
                precuedStartSecRef.current = precueStart;
                precuedDeckRef.current = inactDeck;
                if (inactDeck === 'A') deckAVideoIdRef.current = nextVid;
                else deckBVideoIdRef.current = nextVid;
              } catch (cueErr) {
                console.warn('Error en pre-cueing post-crossfade:', cueErr);
              }
            }
          }
        }, 400);

        // Watchdog rápido (1.0s): verificar que el deck nuevo realmente está reproduciendo
        setTimeout(() => {
          try {
            const activeP = targetDeck === 'A' ? playerRefA.current : playerRefB.current;
            if (activeP && typeof activeP.getPlayerState === 'function') {
              const st = activeP.getPlayerState();
              if (st === -1 || st === 3 || st === 5 || st === 2) {
                console.warn('[Crossfade Watchdog] Deck', targetDeck, 'en estado', st, '— forzando playVideo inmediato');
                if (typeof activeP.unMute === 'function') activeP.unMute();
                if (typeof activeP.playVideo === 'function') activeP.playVideo();
              }
            }
          } catch (e) {}
        }, 1000);

        // Watchdog suave (2.5s): si el deck quedó pausado o cued, asegurar playVideo sin recargar destructivamente
        setTimeout(() => {
          try {
            const activeP = targetDeck === 'A' ? playerRefA.current : playerRefB.current;
            if (activeP && typeof activeP.getPlayerState === 'function') {
              const st = activeP.getPlayerState();
              if (st === 2 || st === 5 || st === -1) { // Pausado, Cued o Unstarted
                console.warn('[Crossfade Watchdog 2.5s] Deck', targetDeck, 'en pausa/cola (st:', st, ') — reanudando playVideo');
                if (typeof activeP.unMute === 'function') activeP.unMute();
                if (typeof activeP.playVideo === 'function') activeP.playVideo();
              }
            }
          } catch (e) {}
        }, 2500);
      }
    }, intervalStepMs);
    fadeTimerRef.current = fadeTimer;
  }, [crossfadeDuration, currentTransitionVideo2, currentIntroVideo2, notifyHost, setVideoId, syncPlaybackState, resetAndStartDeck]);

  /* eslint-disable */

  useEffect(() => {
    const handleMessage = (event) => {
      // Prevenir loops: ignorar mensajes originados por la propia ventana
      if (event.source === window) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data._fromCrossfade || data.type === 'onDeckChanged' || data.type === 'onMidpointCrossfade' || data.type === 'onSongError') {
        return;
      }
      if (data.message) setMessage(data.message);

      switch (data.type) {
        case 'START_CROSSFADE':
          if (data.incomingVid) {
            startCrossfade(
              data.incomingVid,
              data.nextSong,
              data.targetDeck,
              data.crossfadeDuration,
              data.startTime,
              data.endTime
            );
          }
          break;
        case 'changeVideo':
          if (data.videoId) {
            const startSec = Math.max(0, Number(data.startTime) || 0);
            const endSec = Math.max(0, Number(data.endTime) || 0);
            if (data.title || data.assignedTo) {
              setCurrentPlaylistItem2({
                playlist: data.playlist || '',
                item: {
                  videoId: data.videoId,
                  title: data.title || '',
                  assignedTo: data.assignedTo || '',
                  startTime: startSec,
                  endTime: endSec,
                  votes: []
                }
              });
            }
            // Si viene del propio crossfade, el video entrante ya está activo, o hay un crossfade en curso, NO recargar el video
            if (data._fromCrossfade || crossfadingRef.current || currentVideoIdRef.current === data.videoId || incomingVideoIdRef.current === data.videoId) {
              if (!data.forceReload) {
                break;
              }
            }
            currentSongStartTimeRef.current = startSec;
            currentSongEndTimeRef.current = endSec;
            playSong(data.videoId, startSec, endSec);
          }
          break;
        case 'changeIntro':
          setCurrentIntroVideo2(data.introId);
          break;  
        case 'changeTransition':
          setCurrentTransitionVideo2(data.transitionId);
          break;   
        case 'changePlaylistItem':
          setCurrentPlaylistItem2(data.playlistItem);
          break;
        case 'setVolume':
          if (data.volume !== undefined) {
            const vol = Math.max(0, Math.min(100, Number(data.volume)));
            setScreenVolume(vol);
            screenVolumeRef.current = vol;
            const p = playerRef.current;
            if (p && typeof p.setVolume === 'function') {
              try {
                p.setVolume(vol);
                if (vol > 0 && typeof p.unMute === 'function') {
                  p.unMute();
                } else if (vol === 0 && typeof p.mute === 'function') {
                  p.mute();
                }
              } catch (err) {
                console.warn('Error applying volume:', err);
              }
            }
          }
          break;
        case 'setPlaybackRate':
          if (data.playbackRate !== undefined) {
            const rate = Number(data.playbackRate);
            setScreenRate(rate);
            screenRateRef.current = rate;
            const p = (typeof getActivePlayer === 'function' ? getActivePlayer() : null) || playerRef.current;
            if (p && typeof p.setPlaybackRate === 'function') {
              try {
                p.setPlaybackRate(rate);
              } catch (err) {
                console.warn('Error applying playback rate:', err);
              }
            }
            if (playerRefA.current && typeof playerRefA.current.setPlaybackRate === 'function') {
              try { playerRefA.current.setPlaybackRate(rate); } catch (e) {}
            }
            if (playerRefB.current && typeof playerRefB.current.setPlaybackRate === 'function') {
              try { playerRefB.current.setPlaybackRate(rate); } catch (e) {}
            }
            // Emitir inmediatamente para que el monitor reciba la nueva velocidad y reanude la interpolación exacta
            if (typeof syncPlaybackState === 'function') {
              syncPlaybackState(playerStatusRef.current || 'PLAYING');
            }
          }
          break;
        case 'setPitch':
          if (data.pitch !== undefined) {
            setScreenPitch(Number(data.pitch));
          }
          break;
        case 'setContinuousPlay':
          if (data.continuousPlay !== undefined) {
            setContinuousPlay(Boolean(data.continuousPlay));
          }
          break;
        case 'setCrossfadeEnabled':
          if (data.crossfadeEnabled !== undefined) {
            const isEnabled = Boolean(data.crossfadeEnabled);
            setCrossfadeEnabled(isEnabled);
            if (!isEnabled) {
              setActiveDeck('A');
              activeDeckRef.current = 'A';
              setTransitionTargetDeck(null);
              setDeckAOpacity(1);
              setDeckBOpacity(0);
              if (playerRefB.current) {
                try {
                  if (typeof playerRefB.current.setVolume === 'function') playerRefB.current.setVolume(0);
                  if (typeof playerRefB.current.stopVideo === 'function') playerRefB.current.stopVideo();
                } catch (e) {}
              }
            }
          }
          break;
        case 'setCrossfadeDuration':
          if (data.crossfadeDuration !== undefined) {
            setCrossfadeDuration(Number(data.crossfadeDuration));
          }
          break;
        case 'setNextSong':
          nextSongRef.current = data.nextSong || null;
          if (data.isEndOfPlaylist !== undefined) {
            isEndOfPlaylistRef.current = Boolean(data.isEndOfPlaylist);
          }
          // PRE-CUEING: Cargar anticipadamente en el deck inactivo para un fundido instantáneo sin buffering
          if (crossfadeEnabled && data.nextSong?.videoId && !crossfadingRef.current) {
            const nextVid = data.nextSong.videoId;
            const inactiveDeck = activeDeckRef.current === 'A' ? 'B' : 'A';
            const inactivePlayer = inactiveDeck === 'A' ? playerRefA.current : playerRefB.current;
            const precueStartSec = Math.max(0, Number(data.nextSong.startTime ?? data.nextSong.item?.startTime ?? data.nextSong.item?.t ?? 0) || 0);

            if (inactivePlayer && typeof inactivePlayer.cueVideoById === 'function' && precuedVideoIdRef.current !== nextVid) {
              console.log(`🎛️ [Pre-cueing setNextSong] Pre-cargando anticipadamente ${nextVid} en Deck ${inactiveDeck} (start: ${precueStartSec}s)...`);
              try {
                if (typeof inactivePlayer.setVolume === 'function') {
                  inactivePlayer.setVolume(0);
                }
                inactivePlayer.cueVideoById({
                  videoId: nextVid,
                  startSeconds: precueStartSec
                });
                precuedVideoIdRef.current = nextVid;
                precuedStartSecRef.current = precueStartSec;
                precuedDeckRef.current = inactiveDeck;
                if (inactiveDeck === 'A') {
                  deckAVideoIdRef.current = nextVid;
                } else {
                  deckBVideoIdRef.current = nextVid;
                }
              } catch (cueErr) {
                console.warn('Error en pre-cueing setNextSong:', cueErr);
              }
            }
          }
          break;
        case 'resetPlayerSettings':
          setScreenVolume(100);
          screenVolumeRef.current = 100;
          setScreenRate(1.0);
          screenRateRef.current = 1.0;
          setScreenPitch(0);
          const pReset = playerRef.current;
          if (pReset) {
            try {
              if (typeof pReset.setVolume === 'function') {
                pReset.setVolume(100);
                pReset.unMute();
              }
              if (typeof pReset.setPlaybackRate === 'function') {
                pReset.setPlaybackRate(1.0);
              }
            } catch (e) {}
          }
          break;
        case 'setOverlay':
          if (!data.overlay) {
            setOpenModal(false);
            setModalContentType('');
          } else {
            if (data.message !== undefined) setMessage(data.message);
            if (data.query !== undefined) setQueryQR(data.query);
            if (data.overlay === 'message') setModalContentType('Message');
            else if (data.overlay === 'qr') setModalContentType('QR');
            else if (data.overlay === 'votes') setModalContentType('Votes');
            setOpenModal(true);
          }
          break;
        case 'showHideModalQR':
          setModalContentType('QR');
          setOpenModal(!data.modalQR);
          if (data.query) setQueryQR(data.query);
          break;  
        case 'showHideModalMessage':
          setModalContentType('Message');
          setOpenModal(!data.modalMessage);
          if (data.message) setMessage(data.message);
          break; 
        case 'showHideModalVotesTable':
          setModalContentType('Votes');
          setOpenModal(!data.modalVotes);
          break;   
        default:;  
      }
    };

    let channel = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        channel = new BroadcastChannel('garaoke_player_sync');
        channel.onmessage = (event) => {
          if (event.data) {
            handleMessage(event);
          }
        };
      }
    } catch (e) {}

    const handleStorage = (e) => {
      if (e.key === 'garaoke_screen_settings' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.volume !== undefined) {
            handleMessage({ data: { type: 'setVolume', volume: parsed.volume } });
          }
          if (parsed.playbackRate !== undefined) {
            handleMessage({ data: { type: 'setPlaybackRate', playbackRate: parsed.playbackRate } });
          }
          if (parsed.pitch !== undefined) {
            handleMessage({ data: { type: 'setPitch', pitch: parsed.pitch } });
          }
        } catch (err) {}
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      if (channel) {
        channel.close();
      }
    };
  }, []); 

  useEffect(() => {
    const vid = queryParams.get('videoId') ?? currentTransitionVideo;
    if (vid) {
      currentVideoIdRef.current = vid;
      setVideoId(vid);
    }
  }, []); 

  /* eslint-enable */

  return (
    <ThemeProvider theme={darkTheme}>
      <div 
        ref={containerRef}
        style={{ 
          height: isIntegrated ? '100%' : '100vh', 
          width: '100%',
          display: 'flex', 
          flexDirection: 'column', 
          background: '#000000', 
          position: 'relative',
          borderRadius: isIntegrated ? '16px' : 0,
          overflow: 'hidden'
        }}
      >
        <PlayerModal 
          open={openModal} 
          onClose={handleCloseModal} 
          type={modalContentType} 
          message={message} 
          queryQR={queryQR}
          userId={effectiveHostId}
        />
        {!isIntegrated && <CssBaseline />}
        {(import.meta.env?.VITE_ENABLE_PITCH_CONTROL === 'true' || process.env?.VITE_ENABLE_PITCH_CONTROL === 'true') && screenPitch !== 0 && (
          <div 
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 9999,
              background: 'rgba(25, 10, 45, 0.85)',
              border: '1px solid rgba(180, 77, 255, 0.7)',
              backdropFilter: 'blur(8px)',
              borderRadius: '20px',
              padding: '6px 14px',
              color: '#ffffff',
              fontSize: '0.84rem',
              fontWeight: 600,
              boxShadow: '0 0 16px rgba(180, 77, 255, 0.45)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>🎵</span>
            <span>Tono: {screenPitch > 0 ? `+${screenPitch}` : screenPitch} semitono{Math.abs(screenPitch) > 1 ? 's' : ''}</span>
          </div>
        )}
        {initialVideoId && (
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            {/* Deck 1 (Deck A) - Siempre en el DOM y en display block para evitar congelamiento de iframe */}
            <div
              id="deck1"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'block',
                opacity: crossfadeEnabled ? deckAOpacity : (activeDeck === 'A' ? 1 : 0),
                visibility: (!crossfadeEnabled || isDeck1InUse) ? 'visible' : 'hidden',
                pointerEvents: (!crossfadeEnabled || activeDeck === 'A' || transitionTargetDeck === 'A') ? 'auto' : 'none',
                transition: 'opacity 0.15s ease-out',
                zIndex: transitionTargetDeck ? (transitionTargetDeck === 'A' ? 3 : 1) : (activeDeck === 'A' ? 2 : 1)
              }}
            >
              <YouTube 
                id="iframe_deck1"
                key="deck1_player_instance"
                videoId={initialDeckAVideoIdRef.current || initialVideoId} 
                onPlay={(e) => onPlayVideo(e, 'A')}
                onPause={(e) => onPauseVideo(e, 'A')}
                opts={opts} 
                onEnd={() => onEndVideo('A')} 
                onEndVideo={() => onEndVideo('A')} 
                onReady={(e) => onReadyVideo(e, 'A')}
                onError={(e) => onErrorVideo(e, 'A')}
                onStateChange={(e) => onStateChangeVideo(e, 'A')}
              />
            </div>

            {/* Deck 2 (Deck B) - Siempre en block mientras crossfade esté activo para mantener el iframe despierto */}
            <div
              id="deck2"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: crossfadeEnabled ? 'block' : 'none',
                opacity: crossfadeEnabled ? deckBOpacity : 0,
                visibility: (crossfadeEnabled && isDeck2InUse) ? 'visible' : 'hidden',
                pointerEvents: (activeDeck === 'B' || transitionTargetDeck === 'B') ? 'auto' : 'none',
                transition: 'opacity 0.15s ease-out',
                zIndex: transitionTargetDeck ? (transitionTargetDeck === 'B' ? 3 : 1) : (activeDeck === 'B' ? 2 : 1)
              }}
            >
              <YouTube 
                id="iframe_deck2"
                key="deck2_player_instance"
                videoId={initialDeckBVideoIdRef.current || defaultFallback} 
                onPlay={(e) => onPlayVideo(e, 'B')}
                onPause={(e) => onPauseVideo(e, 'B')}
                opts={opts} 
                onEnd={() => onEndVideo('B')} 
                onEndVideo={() => onEndVideo('B')} 
                onReady={(e) => onReadyVideo(e, 'B')}
                onError={(e) => onErrorVideo(e, 'B')}
                onStateChange={(e) => onStateChangeVideo(e, 'B')}
              />
            </div>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Player;
